// Container Trader: "Become a container dealer" page (/p/become-a-dealer).
// Built entirely in code, so it does not need a matching page in Sharetribe Console.
// Pitches established local businesses (equipment dealers, lumber yards, farm and ranch stores)
// on adding containers through Dealer Pro. Plan price lives in ../CtSellPlans/ctSellPlansConfig.js.
export { PRO_APPLY_URL } from '../CtSellPlans/ctSellPlansConfig';

export const NEW_DEALER_PAGE_ID = 'become-a-dealer';

export const NEW_DEALER_META = {
  title: 'Become a Shipping Container Dealer | Container Trader',
  description:
    'Equipment dealers, lumber yards and farm and ranch stores: sell shipping containers to the customers you already serve. Wholesale inventory, drop-ship or yard delivery. Established businesses only.',
};

// Synthetic page asset so PageBuilder renders this page without a Console asset.
export const newDealerPageData = () => ({
  meta: {
    pageTitle: { fieldType: 'metaTitle', content: NEW_DEALER_META.title },
    pageDescription: { fieldType: 'metaDescription', content: NEW_DEALER_META.description },
    socialSharing: {
      fieldType: 'openGraphData',
      title: NEW_DEALER_META.title,
      description: NEW_DEALER_META.description,
    },
  },
  sections: [
    { sectionId: 'dealer-hero', sectionType: 'ctNewDealerHero' },
    { sectionId: 'dealer-who', sectionType: 'ctNewDealerWho' },
    { sectionId: 'dealer-why', sectionType: 'ctNewDealerWhy' },
    { sectionId: 'dealer-earn', sectionType: 'ctNewDealerEarn' },
    { sectionId: 'dealer-wholesale-ticker', sectionType: 'ctNewDealerTicker' },
    { sectionId: 'dealer-delivery', sectionType: 'ctNewDealerDelivery' },
    { sectionId: 'dealer-qualify', sectionType: 'ctNewDealerQualify' },
    { sectionId: 'dealer-how', sectionType: 'ctNewDealerHow' },
    { sectionId: 'dealer-plan', sectionType: 'ctNewDealerPlan' },
    { sectionId: 'dealer-faq', sectionType: 'ctNewDealerFaq' },
    { sectionId: 'dealer-cta', sectionType: 'ctNewDealerCta' },
  ],
});

export const TARGET_BUSINESSES = [
  'Heavy equipment dealers',
  'Lumber yards',
  'Farm and ranch stores',
  'Tractor and ag equipment dealers',
  'Equipment rental yards',
  'Building supply and hardware stores',
  'Trailer dealers',
  'Feed and farm supply stores',
];

export const WHY = [
  {
    icon: 'chat',
    title: 'Your customers already need them',
    text:
      'Contractors, farmers and ranchers use containers for tool storage, feed, equipment and job sites. Offer containers to people who already buy from you.',
  },
  {
    icon: 'search',
    title: 'Wholesale inventory nationwide',
    text:
      'Dealer Pro members can browse and request containers from approved wholesale suppliers, including in markets where you have no stock of your own.',
  },
  {
    icon: 'truck',
    title: 'No yard space required',
    text:
      'Have containers delivered straight to your customer, stock a few on your lot, or post the haul on our load board and pick an approved carrier. Choose what fits each sale.',
  },
  {
    icon: 'shield',
    title: 'Backed by container people',
    text:
      'Our team has been in trucking since 1998 and in shipping containers since 2006, and we help new dealers get set up.',
  },
];

// How Dealer Pro makes money for the store: buy wholesale, price retail, list online.
export const EARN = [
  {
    icon: 'search',
    tag: 'Buy',
    title: 'Buy at wholesale prices',
    text:
      'Dealer Pro members buy containers from our approved wholesale suppliers at wholesale prices, not retail. Request only what you need, when you need it.',
  },
  {
    icon: 'card',
    tag: 'Price',
    title: 'Set your own retail prices',
    text:
      'You decide what your customers pay. Price containers to fit your market and keep the difference between your wholesale cost and your retail price.',
  },
  {
    icon: 'cart',
    tag: 'List',
    title: 'Bring new customers to your store',
    text:
      'Post your containers on Container Trader. Buyers searching for containers near them find your listings and contact you, bringing new customers to your store. Approved inventory can also be eligible for Buy It Now.',
  },
];

export const DELIVERY_OPTIONS = [
  {
    id: 'direct',
    tag: 'No inventory',
    title: 'Deliver straight to your customer',
    lead:
      'The container goes from the supplier’s depot directly to your customer’s site. You never handle it.',
    items: [
      'Standard tilt-deck delivery rates',
      'Priced by road miles from the depot to your customer',
      'No yard space, forklift or trailer needed',
      'Best for one-off sales and customers outside your area',
    ],
  },
  {
    id: 'yard',
    tag: 'Stock your lot',
    title: 'Deliver to your location',
    lead:
      'Containers come to your store or yard at a fixed per-container rate, so you can keep stock on hand.',
    items: [
      'Fixed per-container rate for your location, quoted up front',
      'Customers can see containers before they buy',
      'Sell from stock without waiting on delivery from a depot',
      'Best if you have room and equipment to load and deliver',
    ],
  },
];

export const QUALIFY = [
  'An established business with a physical location: a storefront, yard or lot',
  'A business license, resale certificate or EIN',
  'A business website or Google Business Profile',
];

export const NOT_ACCEPTED = [
  'Craigslist or Facebook Marketplace flippers',
  'Home-based resellers without a business location',
  'Brokers or middlemen without a physical location',
  'New businesses without a license or tax ID',
];

export const STEPS = [
  ['Apply', 'Tell us about your business, location and the customers you serve.'],
  [
    'Verification',
    'We confirm your business address, license or EIN, and website or Google listing.',
  ],
  ['Setup', 'Once approved, we send a secure payment link and set up your Dealer Pro account.'],
  [
    'Start selling',
    'Browse wholesale inventory, request containers and choose how each one is delivered, including posting loads for approved carriers on the load board.',
  ],
];

export const FAQ = [
  [
    'Do I need experience selling containers?',
    'No. This program is built for businesses adding containers to what they already sell. We walk you through setup and are a phone call away when a customer has a question.',
  ],
  [
    'Why do you only accept established businesses?',
    'Wholesale suppliers trust us to send their inventory to real dealers. Requiring a physical location and business credentials protects suppliers, buyers and the dealers in our network.',
  ],
  [
    'Do I have to keep containers on my lot?',
    'No. You can have containers delivered directly to your customer at standard tilt-deck rates, or delivered to your location at a fixed per-container rate when you want stock on hand. You choose for each order.',
  ],
  [
    'How do I buy wholesale containers?',
    'Dealer Pro members browse available wholesale stock and send a purchase request. The supplier or Container Trader confirms the request, then arranges payment and delivery with you.',
  ],
  [
    'Can I find a truck to move containers?',
    'Yes. Dealer Pro includes the Container Trader load board. Post a load with the pickup, drop-off and your rate, and approved carriers with a USDOT number send requests. You choose the carrier and agree the details with them directly.',
  ],
  [
    'Who sets the price my customer pays?',
    'You do. Dealer Pro members buy at wholesale prices and set their own retail prices, both in your store and on your Container Trader listings.',
  ],
  [
    'Can I list my containers on Container Trader?',
    'Yes. Dealer Pro includes unlimited active listings and a company profile, so buyers searching in your area can find your containers and contact your store.',
  ],
  [
    'How long does approval take?',
    'We review every application by hand. If anything is missing, we contact you to finish verification before approval.',
  ],
];
